/**
 * @fileoverview Input/output schemas for the prose-continuer-discovery-neutral agent.
 *
 * A reduced variant of prose-continuer-discovery that focuses on character
 * momentum and narrative flow while writing in deliberately plain, neutral
 * prose. Style, voice, and tone are intentionally deferred to a downstream
 * stylization pass.
 *
 * Input/output shapes match prose-continuer-discovery exactly so callers can
 * swap between variants without adapting their payloads.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.seriesContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.styleGuide.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingGenreGuidance.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.existingProse.schema)
  .merge(AGENT_SCHEMA_OBJECTS.lastThreeChaptersContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.writingStyleSample.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingHistory.schema)
  .merge(AGENT_SCHEMA_OBJECTS.isShortForm.schema)
  .merge(z.object({
    actualWordCount: z.number().optional().describe('Current word count of the existing prose.'),
    wordBudgetRemaining: z.number().optional().describe('Approximate words remaining before reaching the projected word count.'),
    isNearingWordBudget: z.boolean().optional().describe('True if the chapter is within 80-100% of its projected word count.'),
    isOverWordBudget: z.boolean().optional().describe('True if the chapter has exceeded its projected word count.'),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining what emerged in your continuation — what character impulses or story momentum you followed (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The newly generated prose continuation in plain neutral prose, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
